/**
 * frontend/src/pages/valuation/_industryFacts.ts
 *
 * VAL-INDUSTRY-FACTS-1C — industry facts: form state <-> server payload.
 * State is all strings (what the inputs hold). A blank is never sent as 0 and
 * a typed value is never dropped: one that is not a number goes as typed, so
 * the server's 422 names it. Nothing left -> null (clears this industry's facts).
 * No React, no field list: the fields come from the server.
 */
import type { IndustryFactField, IndustryFacts } from '@/types/valuationCompany'

export type FactsState = Record<string, string>

const NUMERIC = new Set<IndustryFactField['type']>(['pct', 'number', 'count'])

export function factsFromSaved(fields: IndustryFactField[], saved: IndustryFacts | null | undefined): FactsState {
  const state: FactsState = {}
  for (const { key } of fields) {
    const v = saved?.[key]
    state[key] = v === undefined || v === null ? '' : v === true ? 'yes' : v === false ? 'no' : String(v)
  }
  return state
}

export function factsToPayload(fields: IndustryFactField[], state: FactsState): IndustryFacts | null {
  const out: IndustryFacts = {}
  for (const { key, type } of fields) {
    const raw = (state[key] ?? '').trim()
    if (raw === '') continue
    if (NUMERIC.has(type)) out[key] = Number.isFinite(Number(raw)) ? Number(raw) : raw
    else if (type === 'yesno') out[key] = raw === 'yes'
    else out[key] = raw
  }
  return Object.keys(out).length ? out : null
}

/** Total per pct group, counting only filled values. */
export function groupTotals(fields: IndustryFactField[], state: FactsState): Record<string, number> {
  const totals: Record<string, number> = {}
  for (const { key, type, group } of fields) {
    const raw = (state[key] ?? '').trim()
    if (type !== 'pct' || !group || raw === '' || !Number.isFinite(Number(raw))) continue
    totals[group] = Math.round(((totals[group] ?? 0) + Number(raw)) * 100) / 100
  }
  return totals
}

export function factsDirty(a: FactsState, b: FactsState): boolean {
  return [...new Set([...Object.keys(a), ...Object.keys(b)])].some(k => (a[k] ?? '') !== (b[k] ?? ''))
}
